import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { PlanSelector } from '@/components/billing/PlanSelector';
import {
  useSubscription,
  useStoredPlan,
  isSubscriptionActive,
  type SubscriptionPlan,
  type BillingPeriod,
} from '@/hooks/useSubscription';
import { useAuth } from '@/contexts/AuthContext';

export function PlanSelectionPage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { subscription } = useSubscription();
  const { setStoredPlan } = useStoredPlan();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);

  // Determine current plan info
  const hasActiveSubscription = subscription?.status && isSubscriptionActive(subscription.status);

  const handleSelectPlan = async (
    planId: string,
    planType: SubscriptionPlan,
    billingPeriod: BillingPeriod
  ) => {
    setLoadingPlanId(planId);

    if (hasActiveSubscription) {
      // Existing subscriber → plan change confirmation page
      navigate({ to: '/app/plan-change', search: { plan: planType, period: billingPeriod } });
    } else {
      // New subscriber → payment collection page
      setStoredPlan(planType, billingPeriod);
      navigate({ to: '/app/complete-payment' });
    }
  };

  const handleLogout = async () => {
    await signOut();
    // Force full reload to clear all state and redirect to landing
    window.location.href = '/';
  };

  const currentPlanId = hasActiveSubscription ? subscription?.current_plan_id : null;
  const currentPlanType = hasActiveSubscription ? subscription?.plan_type : null;

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      <main className="flex-1 py-12">
        <div className="container">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Choose Your Plan
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Select a plan to get started. All plans include every feature.
            </p>

            {user && (
              <p className="mt-2 text-sm text-muted-foreground">
                Signed in as <span className="font-medium">{user.email}</span>
                {' · '}
                <button
                  onClick={handleLogout}
                  className="text-sapling-primary-200 hover:text-sapling-primary-100"
                >
                  Sign out
                </button>
              </p>
            )}
          </div>

          <div className="mt-12">
            <PlanSelector
              currentPlanId={currentPlanId}
              currentPlanType={currentPlanType}
              onSelectPlan={handleSelectPlan}
              loading={!!loadingPlanId}
              loadingPlanId={loadingPlanId}
            />
          </div>

          <div className="mx-auto mt-12 max-w-2xl text-center">
            <p className="text-sm text-muted-foreground">
              Cancel anytime. Questions? Contact us at{' '}
              <a
                href="mailto:support@oasisautokonnect.com"
                className="text-sapling-primary-200 hover:text-sapling-primary-100"
              >
                support@oasisautokonnect.com
              </a>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
